import { useEffect, useMemo, useRef } from "react";
import { AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import { GoogleMapWrapper } from "@/components/map/GoogleMapWrapper";
import { cn } from "@/lib/utils";
import { verdictColor, type CityReportStation } from "./types";

interface StationMapProps {
  stations: CityReportStation[];
  selectedStationId: string | null;
  onSelect: (id: string) => void;
}

function StationMapContent({
  stations,
  selectedStationId,
  onSelect,
}: StationMapProps) {
  const map = useMap();
  const hasFitBoundsRef = useRef(false);

  useEffect(() => {
    if (!map || stations.length === 0) return;
    if (hasFitBoundsRef.current) return;
    const bounds = new google.maps.LatLngBounds();
    for (const s of stations) {
      bounds.extend({ lat: s.lat, lng: s.lng });
    }
    if (stations.length === 1) {
      map.panTo({ lat: stations[0]!.lat, lng: stations[0]!.lng });
      map.setZoom(15);
    } else {
      map.fitBounds(bounds, 48);
    }
    hasFitBoundsRef.current = true;
  }, [map, stations]);

  return (
    <>
      {stations.map((s) => {
        const color = verdictColor(s.city_verdict);
        const isSelected = s.id === selectedStationId;
        return (
          <AdvancedMarker
            key={s.id}
            position={{ lat: s.lat, lng: s.lng }}
            title={s.label}
            onClick={() => onSelect(s.id)}
            zIndex={isSelected ? 1000 : 500}
          >
            <div
              className={cn(
                "relative flex items-center justify-center transition-transform",
                isSelected ? "scale-125" : "hover:scale-110",
              )}
            >
              <svg width="30" height="42" viewBox="0 0 30 42">
                <path
                  d="M15 0C6.716 0 0 6.716 0 15c0 11.25 15 27 15 27s15-15.75 15-27C30 6.716 23.284 0 15 0z"
                  fill={color}
                  stroke="white"
                  strokeWidth="1.5"
                />
                <circle cx="15" cy="15" r="5" fill="white" />
              </svg>
            </div>
          </AdvancedMarker>
        );
      })}
    </>
  );
}

export function StationMap(props: StationMapProps) {
  const center = useMemo(() => {
    if (props.stations.length === 0) return undefined;
    const lat =
      props.stations.reduce((s, x) => s + x.lat, 0) / props.stations.length;
    const lng =
      props.stations.reduce((s, x) => s + x.lng, 0) / props.stations.length;
    return { lat, lng };
  }, [props.stations]);

  return (
    <GoogleMapWrapper
      defaultCenter={center}
      defaultZoom={props.stations.length > 0 ? 12 : 7}
      className="h-full w-full"
    >
      <StationMapContent {...props} />
    </GoogleMapWrapper>
  );
}
